import React, { useState, useEffect } from "react";
import { SettingsModalProps, DogFormData, ActivityConfig } from "../../types";
import { getActivityTypeOptions, getFeedbackTypeOptions } from "../../utils/activityUtils";
import { useActivities } from "../../hooks/useActivities";

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, onRefresh, dogProfile }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [editingActivity, setEditingActivity] = useState<ActivityConfig | null>(null);
  const [dogData, setDogData] = useState<DogFormData>({
    name: "",
    breed: "",
    age: 0,
    weight: 0,
    imageBase64: ""
  });

  const { activities, createActivity, updateActivity, deleteActivity } = useActivities();

  useEffect(() => {
    if (isOpen && dogProfile) {
      setDogData({
        name: dogProfile.name || "",
        breed: dogProfile.breed || "",
        age: dogProfile.age || 0,
        weight: dogProfile.weight || 0,
        imageBase64: dogProfile.imageBase64 || ""
      });
    }
  }, [isOpen, dogProfile]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setDogData({ ...dogData, imageBase64: e.target?.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveDogProfile = async () => {
    try {
      // Note: This would need to be implemented in the hook
      // await updateDogProfile(dogData);
      onRefresh();
      alert("Dog profile updated successfully!");
    } catch (error) {
      console.error("Error updating dog profile:", error);
    }
  };

  const handleSaveActivity = async () => {
    if (!editingActivity) return;
    
    try {
      if (editingActivity.id) {
        await updateActivity(editingActivity.id, editingActivity);
      } else {
        await createActivity(editingActivity);
      }
      setEditingActivity(null);
      onRefresh();
    } catch (error) {
      console.error("Error saving activity:", error);
    }
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (window.confirm("Are you sure you want to delete this activity?")) {
      try {
        await deleteActivity(activityId);
        onRefresh();
      } catch (error) {
        console.error("Error deleting activity:", error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-6 border-b">
          <button
            onClick={() => setActiveTab("profile")}
            className={`pb-2 px-4 font-medium ${
              activeTab === "profile" 
                ? "border-b-2 border-blue-500 text-blue-600" 
                : "text-gray-600"
            }`}
          >
            Dog Profile
          </button>
          <button
            onClick={() => setActiveTab("activities")}
            className={`pb-2 px-4 font-medium ${
              activeTab === "activities" 
                ? "border-b-2 border-blue-500 text-blue-600" 
                : "text-gray-600"
            }`}
          >
            Activities
          </button>
        </div>

        {/* Dog Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {dogData.imageBase64 ? (
                    <img 
                      src={dogData.imageBase64} 
                      alt="Dog" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl">🐕</span>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-2 cursor-pointer hover:bg-blue-600">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  📷
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={dogData.name}
                  onChange={(e) => setDogData({ ...dogData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Breed</label>
                <input
                  type="text"
                  value={dogData.breed}
                  onChange={(e) => setDogData({ ...dogData, breed: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Age (years)</label>
                <input
                  type="number"
                  value={dogData.age}
                  onChange={(e) => setDogData({ ...dogData, age: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={dogData.weight}
                  onChange={(e) => setDogData({ ...dogData, weight: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <button
              onClick={handleSaveDogProfile}
              className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Save Profile
            </button>
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === "activities" && (
          <div className="space-y-4">
            <button
              onClick={() => setEditingActivity({
                name: "",
                timerType: "walk",
                customName: "",
                icon: "🚶",
                color: "blue",
                scheduleType: "interval",
                intervalHours: 8,
                specificTimes: [],
                feedbackTypes: ["notes"],
                isActive: true
              })}
              className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              Add New Activity
            </button>
            
            <div className="space-y-2">
              {activities.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{activity.icon}</span>
                    <div>
                      <div className="font-medium">{activity.name}</div>
                      <div className="text-sm text-gray-600">
                        {activity.scheduleType === "interval" 
                          ? `Every ${activity.intervalHours}h`
                          : `At ${activity.specificTimes.join(", ")}`
                        }
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingActivity(activity)}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => activity.id && handleDeleteActivity(activity.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Edit Modal */}
        {editingActivity && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">
                {editingActivity.id ? "Edit Activity" : "Add New Activity"}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input
                    type="text"
                    value={editingActivity.name}
                    onChange={(e) => setEditingActivity({ ...editingActivity, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={editingActivity.timerType}
                    onChange={(e) => setEditingActivity({ ...editingActivity, timerType: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {getActivityTypeOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.icon} {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Icon</label>
                  <input
                    type="text"
                    value={editingActivity.icon}
                    onChange={(e) => setEditingActivity({ ...editingActivity, icon: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="🚶"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Schedule Type</label>
                  <select
                    value={editingActivity.scheduleType}
                    onChange={(e) => setEditingActivity({ ...editingActivity, scheduleType: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="interval">Interval (Every X hours)</option>
                    <option value="specific_times">Specific Times</option>
                  </select>
                </div>

                {editingActivity.scheduleType === "interval" && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Interval (hours)</label>
                    <input
                      type="number"
                      value={editingActivity.intervalHours}
                      onChange={(e) => setEditingActivity({ ...editingActivity, intervalHours: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                )}

                {editingActivity.scheduleType === "specific_times" && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Specific Times (comma-separated, e.g., 07:00, 19:00)</label>
                    <input
                      type="text"
                      value={editingActivity.specificTimes.join(", ")}
                      onChange={(e) => setEditingActivity({ 
                        ...editingActivity, 
                        specificTimes: e.target.value.split(",").map(t => t.trim()).filter(t => t)
                      })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="07:00, 19:00"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Feedback Types</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getFeedbackTypeOptions().map(option => (
                      <label key={option.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editingActivity.feedbackTypes.includes(option.value)}
                          onChange={(e) => {
                            const newFeedbackTypes = e.target.checked
                              ? [...editingActivity.feedbackTypes, option.value]
                              : editingActivity.feedbackTypes.filter(type => type !== option.value);
                            setEditingActivity({ ...editingActivity, feedbackTypes: newFeedbackTypes });
                          }}
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingActivity.isActive}
                    onChange={(e) => setEditingActivity({ ...editingActivity, isActive: e.target.checked })}
                  />
                  <label className="text-sm">Active</label>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setEditingActivity(null)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveActivity}
                  className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsModal;